import type { Broker } from '@/services';
import type { GroupLifeQuoteDetails } from '@/services/group-life';
import type { GroupLifeCommercialLetterPayload } from '@/services/proposals/group-life';
import { DateTime } from 'luxon';

const DEFAULT_BRANCH = '0001';
const DEFAULT_AFFILIATE = 'Matriz';
const DEFAULT_PERCENT = '0%';

function formatPercent(value: string | number | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return raw.endsWith('%') ? raw : `${raw}%`;
}

function percentOrDefault(
  ...values: Array<string | number | null | undefined>
): string {
  for (const value of values) {
    const normalized = formatPercent(value);
    if (normalized) return normalized;
  }
  return DEFAULT_PERCENT;
}

function parseDate(value?: string): DateTime | null {
  if (!value) return null;
  const iso = DateTime.fromISO(value);
  if (iso.isValid) return iso;
  const br = DateTime.fromFormat(value, 'dd/MM/yyyy');
  return br.isValid ? br : null;
}

function toBrDate(value?: string): string {
  const dt = parseDate(value);
  return dt ? dt.toFormat('dd/MM/yyyy') : (value ?? '');
}

function policyTypeLineLabel(type?: '1' | '3' | '4'): string {
  switch (type) {
    case '3':
      return 'Renovação';
    case '4':
      return 'Transferência';
    case '1':
    default:
      return 'Novo';
  }
}

export type BuildGroupLifeCommercialLetterInput = {
  proposalNumber?: string;
  effectiveDate?: string;
  details?: GroupLifeQuoteDetails;
  broker?: Broker | null;
};

export function buildGroupLifeCommercialLetterPayload(
  input: BuildGroupLifeCommercialLetterInput = {}
): GroupLifeCommercialLetterPayload {
  const { details, broker } = input;
  const stipulator = details?.stipulator;
  const calculation = details?.calculation;
  const commercialization = details?.commercialization;

  // Início da vigência vem do passo 1; se não foi informado (o passo avança
  // mesmo sem data), usamos amanhã como padrão — coerente com o minDate do
  // passo 1 (hoje + 1 dia) — para não quebrar a validação do backend.
  const start =
    parseDate(input.effectiveDate) ?? DateTime.now().plus({ days: 1 });
  const policyStart = start.toFormat('dd/MM/yyyy');
  const policyEnd = start.plus({ years: 1 }).toFormat('dd/MM/yyyy');

  // Nome e SUSEP da corretora são sobrescritos pelo BFF com os dados da
  // APÓLICE (ver `GenerateGroupLifeCommercialLetterPdfHandler`): quem dispara
  // a geração nem sempre tem corretora na sessão — na aprovação da análise
  // quem clica é o subscritor. O que vai daqui é só um palpite.
  const brokerName = broker?.name || '';
  const brokerSusep = broker?.susepCode || broker?.licenseNumber || '';
  // Convenção do InsureMO no PME: `CommissionRate` carrega a CORRETAGEM e
  // `BrokerageRate` carrega o AGENCIAMENTO (ver `applyCommercialization` no
  // BFF). Não há fallback cruzado entre os dois: cada um cai só nos próprios
  // aliases, senão a carta imprimiria agenciamento no lugar da corretagem.
  const brokeragePercent = percentOrDefault(
    commercialization?.brokeragePercent
  );
  const agencingPercent = percentOrDefault(
    commercialization?.agencingPercent,
    commercialization?.agencyPercent
  );

  return {
    proposal: {
      Branch: DEFAULT_BRANCH,
      Affiliate: DEFAULT_AFFILIATE,
      Number: input.proposalNumber || details?.proposalNumber || '',
      // Proposta clonada ainda sem recálculo não tem `calculation` — a
      // validade top-level (StatusExpirationDate/ExpiryDate) cobre o caso.
      ValidUntil: toBrDate(calculation?.validity || details?.validity),
      PolicyStart: policyStart,
      PolicyEnd: policyEnd,
      PolicyTypeLine: policyTypeLineLabel(stipulator?.insuranceType),
      CommissionRate: brokeragePercent,
      BrokerageRate: agencingPercent,
    },
    broker: {
      Name: brokerName,
      SusepCode: brokerSusep,
      ...(commercialization?.coBrokers?.length
        ? {
            Participation: formatPercent(
              commercialization.leaderSharePercent || '100,00'
            ),
          }
        : {}),
    },
    intermediaryBroker: {
      Name: brokerName,
      SusepCode: brokerSusep,
      Commission: brokeragePercent,
      BrokerageRate: agencingPercent,
    },
    ...(commercialization?.coBrokers?.length
      ? {
          coBrokers: commercialization.coBrokers.map(coBroker => ({
            Name: coBroker.name,
            CNPJ: coBroker.registerNumber || '',
            SusepCode: coBroker.susepCode || '',
            Participation: formatPercent(coBroker.sharePercent),
          })),
        }
      : {}),
    stipulant: {
      CompanyName: stipulator?.tradeName || stipulator?.responsibleName || '',
      CNPJ: stipulator?.registerNumber || '',
    },
  };
}
